// src/sagas/collectionSaga.js
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import {
  FETCH_COLLECTIONS_REQUEST,
  CREATE_COLLECTION_REQUEST,
  UPDATE_COLLECTION_REQUEST,
  DELETE_COLLECTION_REQUEST,
} from "../actions/types";
import {
  fetchCollectionsSuccess,
  fetchCollectionsFailure,
  createCollectionSuccess,
  createCollectionFailure,
  updateCollectionSuccess,
  updateCollectionFailure,
  deleteCollectionSuccess,
  deleteCollectionFailure,
} from "../actions/collectionActions";
import {
  fetchLibraryCardsFromApi,
  saveLibraryCardToApi,
  deleteLibraryCardFromApi,
} from "../apis/libraryCardApi";
import { getTenantId } from "../utils/authUtils";

function* fetchCollectionsSaga(action) {
  try {
    const tenantId = action.payload?.tenantId || getTenantId();
    const cards = yield call(fetchLibraryCardsFromApi, { tenantId, entityType: "ARTWORK_COLLECTION" });
    const collections = cards.map((card) => ({
      ...card,
      ...(typeof card.payload === "string" ? JSON.parse(card.payload || "{}") : card.payload || {}),
    }));
    // Sort by displayOrder ascending, then name
    collections.sort((a, b) => {
      const da = a.displayOrder ?? 999;
      const db = b.displayOrder ?? 999;
      if (da !== db) return da - db;
      return (a.name || "").localeCompare(b.name || "");
    });
    yield put(fetchCollectionsSuccess(collections));
  } catch (err) {
    console.error("collectionSaga - fetchCollections error:", err);
    yield put(fetchCollectionsFailure(err.message));
  }
}

function* createCollectionSaga(action) {
  try {
    const tenantId = getTenantId();
    const { name, slug, description, displayOrder } = action.payload;
    const input = {
      tenantId,
      entityType: "ARTWORK_COLLECTION",
      entityId: slug,
      payload: JSON.stringify({ name, slug, description: description || "", displayOrder: displayOrder || 0 }),
    };
    const result = yield call(saveLibraryCardToApi, input);
    if (!result?.success) throw new Error(result?.message || "Save failed");
    yield put(createCollectionSuccess({
      entityId: slug,
      entityType: "ARTWORK_COLLECTION",
      tenantId,
      ...action.payload,
    }));
  } catch (err) {
    console.error("collectionSaga - createCollection error:", err);
    yield put(createCollectionFailure(err.message));
  }
}

function* updateCollectionSaga(action) {
  try {
    const tenantId = getTenantId();
    const { slug, name, description, displayOrder } = action.payload;
    const input = {
      tenantId,
      entityType: "ARTWORK_COLLECTION",
      entityId: slug,
      payload: JSON.stringify({ name, slug, description: description || "", displayOrder: displayOrder || 0 }),
    };
    const result = yield call(saveLibraryCardToApi, input);
    if (!result?.success) throw new Error(result?.message || "Save failed");
    yield put(updateCollectionSuccess(action.payload));
  } catch (err) {
    console.error("collectionSaga - updateCollection error:", err);
    yield put(updateCollectionFailure(err.message));
  }
}

function* deleteCollectionSaga(action) {
  try {
    const tenantId = getTenantId();
    const { slug } = action.payload;
    yield call(deleteLibraryCardFromApi, { tenantId, entityType: "ARTWORK_COLLECTION", entityId: slug });
    yield put(deleteCollectionSuccess(slug));
  } catch (err) {
    console.error("collectionSaga - deleteCollection error:", err);
    yield put(deleteCollectionFailure(err.message));
  }
}

export default function* collectionSaga() {
  yield takeLatest(FETCH_COLLECTIONS_REQUEST, fetchCollectionsSaga);
  yield takeEvery(CREATE_COLLECTION_REQUEST, createCollectionSaga);
  yield takeEvery(UPDATE_COLLECTION_REQUEST, updateCollectionSaga);
  yield takeEvery(DELETE_COLLECTION_REQUEST, deleteCollectionSaga);
}
